import React, { useState } from 'react'
import { useUser } from 'util/UserContext'
import Vote from './Vote'
import Save from './Save'
import './EntityActions.css'
import { Issue, Impact, Solution, EntityType, Comment, Feedback, Influence, Assessment, Meeting } from 'util/constants'
import { MessageSquare, Share, Link } from 'lucide-react'
import { Drawer, DrawerContent, DrawerTrigger } from 'ui/Drawer'
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuItem, DropdownMenuContent } from 'ui/DropdownMenu'
import { toast } from 'sonner'

interface Props {
    entity: Issue | Solution | Impact | Comment | Feedback | Influence | Assessment | Meeting
    entityType: EntityType
    onVote?: () => void
    onSave?: () => void
}

const EntityActions = ({
    entity,
    entityType,
    onVote,
    onSave,
}: Props) => {
    const { currentUser, userIsMobile } = useUser();
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)
    const entityId = entityType === 'issue' 
        ? (entity as Issue).cname
        : (entity as Solution | Impact | Comment | Feedback | Influence | Assessment | Meeting).id
    const url = `${window.location.origin}/${entityType}/${entityId}`

    const handleShare = () => {
        navigator.clipboard.writeText(url).then(() => {
            if (userIsMobile) {
                setDrawerOpen(false)
            } else {
                setMenuOpen(false)
            }
            toast.success('Copied to clipboard!')
        })
    }

    const handleComment = () => {
        if (window.location.href === `${url}/comments`) {
            const commentsSection = document.getElementsByClassName('comment-title')[0]
            if (commentsSection) {
                commentsSection.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                    inline: 'nearest',
                })
            }
        } else {
            window.location.href = `${url}/comments`
        }
    }

    const ShareButton = React.forwardRef<
        HTMLButtonElement,
        React.ButtonHTMLAttributes<HTMLButtonElement>
    >((props, ref) => (
        <button
            type="button"
            ref={ref}
            {...props}
            className="size-9 rounded-full text-eoe-gray dark:text-eoe-dark-icon-stroke dark:hover:text-eoe-dark-icon-stroke-hover bg-gray-200 dark:bg-eoe-dark-icon flex justify-center items-center hover:bg-gray-300 dark:hover:bg-eoe-dark-icon-hover transition-colors"
        >
            <Share className="size-5"/>
        </button>
    ))
    ShareButton.displayName = 'ShareButton'

    const ShareOptions = () => (
        <div onClick={handleShare} className="flex w-full items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
            <Link className="size-4"/>
            <span>Copy link</span>
        </div>
    )

    return (
        <div className={`${entityType !== 'comment' ? 'entity-actions flex items-center mt-2' : 'ignore-card-click entity-actions flex items-center md:flex-row'}`}>
            <Vote
                entityId={entity.id}
                onVote={onVote}
                voteCount={entity.vote_count || 0}
                entityType={entityType}
            />
            <div className="action comment flex text-xl grow">
                {entityType !== 'comment' &&
                <button
                    className="size-9 rounded-full text-eoe-gray dark:text-eoe-dark-icon-stroke dark:hover:text-eoe-dark-icon-stroke-hover bg-gray-200 dark:bg-eoe-dark-icon flex justify-center items-center hover:bg-gray-300 dark:hover:bg-eoe-dark-icon-hover transition-colors"
                    onClick={handleComment}
                >
                    <MessageSquare className="size-5"/>
                </button>
                }
            </div>
            {entityType !== 'comment' && (
                <>
                    <div className="action save flex text-xl relative mx-2">
                        <Save
                            entityId={entity.id}
                            entityType={entityType}
                            currentUser={currentUser}
                            onSave={onSave}
                        />
                    </div>

                    <div className="action share text-xl relative">
                        {userIsMobile ? (
                            <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
                                <DrawerTrigger asChild>
                                    <ShareButton />
                                </DrawerTrigger>
                                <DrawerContent title="Share">
                                    <ShareOptions />
                                </DrawerContent>
                            </Drawer>
                        ) : (
                            <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen} modal={false}>
                                <DropdownMenuTrigger asChild>
                                    <ShareButton />
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    <DropdownMenuItem>
                                        <ShareOptions />
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                    </div>
                </>
            )}
        </div>
    )
}

export default EntityActions
